import React, { useState, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import dayjs from 'dayjs';
import {
  TH_SX,
  TH_GROUP_SX,
  TH_GROUP2_SX,
  TH_GROUP3_SX,
  TH_GROUP4_SX,
  TH_GROUP5_SX,
  TD_SX,
  COL_DEFAULTS,
} from './constants';
import {
  getBgColumnValues,
  getPaidValue,
  getExpectedValue,
  getProjectedFund,
  getRefundPendingValue,
  getEstimatedProfit,
  getPoNetOfVat,
  getTargetProfit,
} from './formulas';
import { fmt } from './utils';
import { ResizeHandle } from './table/cells';
import ProjectDashboardRow from './table/ProjectDashboardRow';
import { confirmAsync } from '../../../utils/functions/globalDialog';

// ── Task 2 ของ session sandbox: clone เต็มรูปแบบของ ProjectDashboardTable.js (ตาราง P5 จริง) ─────
//
// สิ่งที่ "reuse ตรงๆ ไม่แก้แม้แต่บรรทัดเดียว" (import จากไฟล์จริง): ProjectDashboardRow.js (แถวข้อมูล
// ทั้งแถว รวมทุกเซลล์/สี/badge/tooltip), table/cells.js (NumCell/ResizeHandle ฯลฯ), formulas.js
// (สูตรการเงินทั้งหมด), constants.js (TH_SX/COL_DEFAULTS/สีกลุ่มคอลัมน์) — สองไฟล์แรกเป็น
// presentational/callback-driven ล้วนๆ (ไม่มี dispatch/api ของตัวเอง) จึงปลอดภัย reuse ตรงได้เลย
//
// สิ่งที่ "พอร์ตมาทั้งฟังก์ชัน" (copy จาก ProjectDashboardTable.js เพราะเป็น pure function ล้วนๆ
// ไม่มี dispatch/api แต่ไม่ได้ export ให้ import ตรงๆ ได้): getFieldValue/compareRowsBySortKey/
// cycleSortByKey/getSortDir/thSortSx/getW/stickyLeftOf/startResize — ตรรกะเหมือนต้นฉบับเป๊ะ
//
// สิ่งที่ "ตัดออกโดยตั้งใจ" (เป็นฟีเจอร์ระดับหน้า ProjectList.js ไม่ใช่ฟีเจอร์ของตัวตารางเอง, หรือไม่มี
// ความหมายเมื่อมีแค่โปรเจกต์เดียว/สองสามแถวจำลอง): แถบค้นหา/สถานะสรุป/ปุ่ม JS-JSID-TEST toggle
// (กรองระหว่างหลายพันโปรเจกต์ — sandbox มีแค่แถวเดียว), ปุ่ม Export Excel (แพทเทิร์นเดียวกับที่แท็บ
// แดชบอร์ดของ sandbox ตัดออกไปแล้วเพราะ "ไม่เกี่ยวกับการลองเล่นตัวเลข"), FilterPopover/HelpDialog
// (ไม่มี state ระดับหน้าให้เปิด — เนื้อหาเดิมมาจาก parent ทั้งหมด)
//
// สิ่งที่ "ทำงานได้จริงแต่ทำให้ปลอดภัย 100%": Action Required (#) — ของจริงยิง api.put/api.delete
// ตรงๆ พร้อม password gate ตอนลบ ในนี้แก้ local state ล้วนๆ (ไม่มี backend ให้เช็ค password จริง จึง
// ตัด password gate ออก เหลือแค่ confirm ธรรมดา — คอมเมนต์อธิบายไว้ตรงจุด)
export function ProjectDashboardTableSandbox({ rows: rowsProp, uiLang = 'en', onApplyPatch }) {
  const L = (th, en) => (uiLang === 'en' ? en : th);
  const rows = rowsProp || [];
  const containerRef = useRef();

  const [showGroup1, setShowGroup1] = useState(true);
  const [showColNums] = useState(false);
  const [colWidths, setColWidths] = useState({});
  const resizeJustHappenedRef = useRef(false);

  const [projectFilter, setProjectFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [excludedProjects, setExcludedProjects] = useState(new Set());
  const [excludedCustomers, setExcludedCustomers] = useState(new Set());
  const [monthFilter, setMonthFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [excludedZeroFields, setExcludedZeroFields] = useState(new Set());
  const [sortCriteria, setSortCriteria] = useState([
    { key: 'progress', dir: 'asc' },
    { key: 'customer', dir: 'asc' },
  ]);
  const [actionDialog, setActionDialog] = useState(null);
  const [actionText, setActionText] = useState('');

  const clickTimerRef = useRef(null);
  const scheduleClick = useCallback((action) => {
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    clickTimerRef.current = setTimeout(() => {
      action();
      clickTimerRef.current = null;
    }, 220);
  }, []);
  const cancelAndRun = useCallback((action) => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    action();
  }, []);

  const toggleExcludedProject = useCallback((pn) => {
    setExcludedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(pn)) next.delete(pn); else next.add(pn);
      return next;
    });
  }, []);
  const toggleExcludedCustomer = useCallback((cn) => {
    setExcludedCustomers((prev) => {
      const next = new Set(prev);
      if (next.has(cn)) next.delete(cn); else next.add(cn);
      return next;
    });
  }, []);
  const toggleZeroExclude = useCallback((key, value) => {
    if ((Number(value) || 0) !== 0) return;
    setExcludedZeroFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const getVal = useCallback((row, field) => row[field], []);
  const getCustomerName = useCallback((r) => r.client_id?.name || r.customer || '', []);

  const startResize = useCallback((colKey, e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const th = e.currentTarget.closest('th');
    const startW = th ? th.offsetWidth : COL_DEFAULTS[colKey] || 80;
    const onMove = (me) => {
      setColWidths((prev) => ({ ...prev, [colKey]: Math.max(40, startW + me.clientX - startX) }));
      resizeJustHappenedRef.current = true;
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      setTimeout(() => { resizeJustHappenedRef.current = false; }, 0);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  const getW = useCallback(
    (col) => Math.max(colWidths[col] || 0, COL_DEFAULTS[col] || 80),
    [colWidths],
  );

  const stickyLeftOf = useMemo(() => {
    const result = { 0: 0 };
    let offset = 38;
    for (const i of [1, 5, 4, 6]) {
      result[i] = offset;
      offset += getW(i);
    }
    return result;
  }, [getW]);
  const frzTh = (col) => (
    stickyLeftOf[col] !== undefined ? { position: 'sticky', left: stickyLeftOf[col], zIndex: 6 } : {}
  );

  // จำลองสูตร "รับเงิน วันที่" (col29/col30) — พอร์ตจาก computeExpectedPaymentDate ใน
  // ProjectDashboardTable.js ตรงๆ (pure function เดิมไม่มี dispatch/api อยู่แล้ว)
  const computeExpectedPaymentDate = useCallback((row) => {
    const deliveryDate = getVal(row, 'delivery_date');
    const creditDays = row.client_id?.credit_days;
    const paymentCycleDay = getVal(row, 'payment_cycle_day');
    if (!deliveryDate || creditDays == null || creditDays === '') return null;
    const base = new Date(deliveryDate);
    base.setDate(base.getDate() + Number(creditDays));
    if (paymentCycleDay == null || paymentCycleDay === '') return base;
    const day = Number(paymentCycleDay);
    const sameMonth = new Date(base.getFullYear(), base.getMonth(), day);
    return sameMonth >= base ? sameMonth : new Date(base.getFullYear(), base.getMonth() + 1, day);
  }, [getVal]);

  // ── sort — พอร์ตจาก getFieldValue/compareRowsBySortKey/cycleSortByKey/getSortDir/thSortSx
  // ใน ProjectDashboardTable.js ตรงๆ (pure function ล้วนๆ ไม่มี dispatch/api อยู่แล้วในต้นฉบับ) ─────
  const getFieldValue = useCallback((key, row) => {
    const merged = row;
    switch (key) {
      case 'po': return getPoNetOfVat(row);
      case 'received': return (row.received ?? 0) + (row.seed_received ?? 0);
      case 'billed_waiting': return (row.billed_waiting ?? 0) + (row.seed_billed_waiting ?? 0);
      case 'po_remaining':
        return getPoNetOfVat(row) - (row.received ?? 0) - (row.seed_received ?? 0)
          - (row.billed_waiting ?? 0) - (row.seed_billed_waiting ?? 0);
      case 'progress': return Number(row.progress) || 0;
      case 'target_profit': return getTargetProfit(row);
      case 'total_budget': return row.total_budget ?? 0;
      case 'projected_fund': return getProjectedFund(row);
      case 'total_expenses': return getPaidValue(row);
      case 'col12_payable_bg_eligible': return getBgColumnValues(merged).col12;
      case 'expected_value': return getExpectedValue(row);
      case 'ap_pending_unpaid': return row.ap_pending_unpaid ?? 0;
      case 'bg_used_pct': return row.bg_used_pct ?? 0;
      case 'over_budget': return getBgColumnValues(merged).col14 ?? 0;
      case 'net_remaining': return getBgColumnValues(row).col15;
      case 'refund_pending': return getRefundPendingValue(row);
      case 'to_spend': return row.to_spend ?? 0;
      case 'col17': return getBgColumnValues(merged).col17;
      case 'oldest_job_month': return row.oldest_job_date ? new Date(row.oldest_job_date).getMonth() + 1 : 0;
      case 'oldest_job_year': return row.oldest_job_date ? new Date(row.oldest_job_date).getFullYear() : 0;
      case 'realprofit': return getEstimatedProfit(merged);
      case 'profit_pct': {
        const po = getPoNetOfVat(row);
        return po > 0 ? (getEstimatedProfit(merged) / po) * 100 : 0;
      }
      case 'delivery_date': {
        const d = getVal(row, 'delivery_date');
        return d ? new Date(d).getTime() : 0;
      }
      case 'credit_days': return row.client_id?.credit_days ?? 0;
      case 'payment_cycle_day': {
        const p = getVal(row, 'payment_cycle_day');
        return p != null && p !== '' ? Number(p) : 0;
      }
      case 'expected_date': {
        const e = computeExpectedPaymentDate(row);
        return e ? e.getTime() : 0;
      }
      case 'days_left': {
        const e = computeExpectedPaymentDate(row);
        if (!e) return 0;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return Math.round((e.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      }
      default: return null;
    }
  }, [getVal, computeExpectedPaymentDate]);

  const compareRowsBySortKey = useCallback((key, a, b) => {
    if (key === 'project_number') return (a.project_number || '').localeCompare(b.project_number || '', 'th', { numeric: true });
    if (key === 'customer') return getCustomerName(a).localeCompare(getCustomerName(b), 'th');
    if (key === 'name') return (a.name || '').localeCompare(b.name || '', 'th');
    const valA = getFieldValue(key, a);
    const valB = getFieldValue(key, b);
    if (valA === null || valB === null) return 0;
    return (Number(valA) || 0) - (Number(valB) || 0);
  }, [getCustomerName, getFieldValue]);

  const cycleSortByKey = useCallback((key) => {
    if (resizeJustHappenedRef.current) return;
    setSortCriteria((prev) => {
      const found = prev.find((s) => s.key === key);
      const currentDir = found?.dir || null;
      const nextDir = currentDir === null ? 'desc' : currentDir === 'desc' ? 'asc' : null;
      let remaining = prev.filter((s) => s.key !== key);
      if (!found && remaining.length >= 2) remaining = [];
      return nextDir ? [...remaining, { key, dir: nextDir }] : remaining;
    });
  }, []);
  const handleHeaderSortClick = useCallback((key) => {
    if (excludedZeroFields.has(key)) {
      setExcludedZeroFields((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      return;
    }
    cycleSortByKey(key);
  }, [cycleSortByKey, excludedZeroFields]);
  const getSortDir = useCallback((key) => sortCriteria.find((s) => s.key === key)?.dir || null, [sortCriteria]);
  const SORT_ICON_SX = { fontSize: 18, color: '#ef4444', position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)' };
  const sortIcon = (key) => (getSortDir(key) ? (getSortDir(key) === 'asc' ? <ArrowDropUpIcon sx={SORT_ICON_SX} /> : <ArrowDropDownIcon sx={SORT_ICON_SX} />) : null);
  const thSortSx = (key, base, hoverBg = '#7dd3fc') => ({
    ...base,
    cursor: 'pointer',
    userSelect: 'none',
    ...(getSortDir(key) && { bgcolor: '#c09614', color: '#fff', borderBottom: '3px solid #d4a017' }),
    ...(excludedZeroFields.has(key) && { bgcolor: '#7c3aed', color: '#fff', borderBottom: 'none' }),
    '&:hover': { bgcolor: excludedZeroFields.has(key) ? '#6d28d9' : getSortDir(key) ? '#9b780f' : hoverBg },
  });

  const renderSeedCell = useCallback(({ field, seedValue, autoValue, redOnNegative = true, width, excludeKey }) => {
    const combined = (Number(autoValue) || 0) + (Number(seedValue) || 0);
    return (
      <TableCell
        key={field}
        sx={{ ...TD_SX, position: 'relative', textAlign: 'right', userSelect: 'none', ...(width !== undefined && { width }) }}
        onContextMenu={excludeKey ? (e) => { e.preventDefault(); toggleZeroExclude(excludeKey, combined); } : undefined}
      >
        <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', fontSize: '0.65rem', color: redOnNegative && combined < 0 ? 'error.main' : 'text.primary' }}>
          {fmt(combined)}
        </Typography>
      </TableCell>
    );
  }, [toggleZeroExclude]);

  const todayReviewStr = dayjs().format('YYYY-MM-DD');

  const displayRows = useMemo(() => {
    let result = rows;
    if (projectFilter) result = result.filter((r) => r.project_number === projectFilter);
    if (customerFilter) result = result.filter((r) => getCustomerName(r).toLowerCase().includes(customerFilter.toLowerCase()));
    if (excludedProjects.size > 0) result = result.filter((r) => !excludedProjects.has(r.project_number));
    if (excludedCustomers.size > 0) result = result.filter((r) => !excludedCustomers.has(getCustomerName(r)));
    if (monthFilter !== '') result = result.filter((r) => (r.oldest_job_date ? new Date(r.oldest_job_date).getMonth() + 1 : null) === monthFilter);
    if (yearFilter !== '') result = result.filter((r) => (r.oldest_job_date ? new Date(r.oldest_job_date).getFullYear() : null) === yearFilter);
    if (excludedZeroFields.size > 0) {
      const zeroKeys = [...excludedZeroFields];
      result = result.filter((r) => !zeroKeys.some((key) => (Number(getFieldValue(key, r)) || 0) === 0));
    }
    result = [...result].sort((a, b) => {
      const aSued = !!getVal(a, 'legal_lawsuit');
      const bSued = !!getVal(b, 'legal_lawsuit');
      if (aSued !== bSued) return aSued ? 1 : -1;
      for (const rule of sortCriteria) {
        const diff = compareRowsBySortKey(rule.key, a, b);
        if (diff !== 0) return rule.dir === 'asc' ? diff : -diff;
      }
      return (a.project_number || '').localeCompare(b.project_number || '', 'th', { numeric: true });
    });
    return result;
  }, [rows, projectFilter, customerFilter, excludedProjects, excludedCustomers, monthFilter, yearFilter,
    excludedZeroFields, getFieldValue, sortCriteria, compareRowsBySortKey, getCustomerName, getVal]);

  const tableMinWidth = useMemo(() => {
    const moneyGroupCols = [11, 10, 17, 15, 14, 19, 13, 20];
    const fixedCols = [1, 2, 36, 3, 5, 4, 6];
    let total = 38 + fixedCols.reduce((a, i) => a + getW(i), 0);
    total += getW(9);
    if (showGroup1) total += [7, 8, 18, 33, 37, 38, 35, 32, 16, 12, 34, 27, 28, 31, 29, 30].reduce((a, i) => a + getW(i), 0);
    total += moneyGroupCols.reduce((a, i) => a + getW(i), 0);
    total += getW(26);
    return total;
  }, [getW, showGroup1]);

  // ── Action Required (#) — ของจริง api.put/api.delete + password gate ตอนลบ ในนี้แก้ local state
  // ของแถวนั้นล้วนๆ ผ่าน onApplyPatch (สำหรับแถวที่เป็นโปรเจกต์ sandbox เอง) — ตัด password gate ออก
  // เพราะไม่มี backend จริงให้ตรวจสอบรหัสผ่าน เหลือแค่ confirm ธรรมดาแทน
  const openActionDialog = useCallback((row) => {
    const current = row?.action_required?.text ? row.action_required : null;
    setActionText(current?.text || '');
    setActionDialog({ row, mode: current ? 'remove' : 'create' });
  }, []);
  const closeActionDialog = useCallback(() => setActionDialog(null), []);
  const submitActionDialog = useCallback(async () => {
    if (!actionDialog?.row) return;
    const isRemove = actionDialog.mode === 'remove';
    if (isRemove && !(await confirmAsync('ปลดป้าย Action required นี้? (sandbox ไม่ต้องใส่รหัสผ่าน)'))) return;
    if (!isRemove && !actionText.trim()) return;
    onApplyPatch?.(actionDialog.row._id, {
      action_required: isRemove ? null : { text: actionText.trim(), createdAt: new Date().toISOString(), createdBy: 'Sandbox' },
    });
    setActionDialog(null);
  }, [actionDialog, actionText, onApplyPatch]);

  const MoneyTh = ({ col, field, base = TH_SX, hover = '#7dd3fc', sortable = true, children, group }) => {
    if (group && !showGroup1) return null;
    const sx = sortable
      ? { ...thSortSx(field, base, hover), width: getW(col), position: 'relative', ...frzTh(col) }
      : { ...base, width: getW(col), position: 'relative', ...frzTh(col) };
    return (
      <TableCell sx={sx} onClick={sortable ? () => handleHeaderSortClick(field) : undefined}>
        <Box sx={{ textAlign: 'center' }}>
          <Box>{children}</Box>
          {sortable && <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>{sortIcon(field)}</Box>}
        </Box>
        <ResizeHandle onStart={(e) => { e.stopPropagation(); startResize(col, e); }} />
      </TableCell>
    );
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {L(
            'ตาราง P5 จำลอง (คอลัมน์/ฟีเจอร์เดียวกับของจริง) — ไม่มีแถบค้นหา/ตัวกรองระดับหน้า เพราะ sandbox มีแค่โปรเจกต์เดียว',
            'Simulated P5 table (same columns/features as the real one) — no page-level search/filter bar, since the sandbox only has one project',
          )}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant={showGroup1 ? 'outlined' : 'contained'} color={showGroup1 ? 'inherit' : 'error'} onClick={() => setShowGroup1((v) => !v)}>
          {showGroup1 ? L('− ซ่อน Col', '− Hide Col') : L('+ แสดง Col', '+ Show Col')}
        </Button>
      </Stack>

      <TableContainer
        ref={containerRef}
        sx={{
          maxHeight: '520px',
          overflowX: 'auto',
          overflowY: 'auto',
          borderRadius: '10px',
          border: '1px solid rgba(0,0,0,0.08)',
        }}
      >
        <Table size="small" sx={{ tableLayout: 'auto', minWidth: tableMinWidth, borderCollapse: 'separate', borderSpacing: 0 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...TH_SX, width: 38, minWidth: 38, maxWidth: 38, position: 'sticky', left: stickyLeftOf[0], zIndex: 6, textAlign: 'center', fontSize: '0.76rem' }}>
                #<br /><span style={{ fontSize: '0.71rem', opacity: 0.7 }}>{displayRows.length}</span>
              </TableCell>
              <TableCell
                sx={{ ...thSortSx('project_number', TH_SX), width: getW(1), ...frzTh(1) }}
                onClick={() => handleHeaderSortClick('project_number')}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box>{L('เลขงาน', 'Job No.')}</Box>
                  <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>{sortIcon('project_number')}</Box>
                </Box>
                <ResizeHandle onStart={(e) => { e.stopPropagation(); startResize(1, e); }} />
              </TableCell>
              <TableCell sx={{ ...TH_SX, width: getW(2), ...frzTh(2) }}>
                {L('สถานะ', 'Status')}
                <ResizeHandle onStart={(e) => startResize(2, e)} />
              </TableCell>
              <TableCell sx={{ ...TH_SX, width: getW(36), textAlign: 'center', ...frzTh(36) }}>
                {L('ตรวจสอบ', 'Review')}
                <ResizeHandle onStart={(e) => startResize(36, e)} />
              </TableCell>
              <TableCell sx={{ ...thSortSx('progress', TH_SX), width: getW(3), ...frzTh(3) }} onClick={() => handleHeaderSortClick('progress')}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box>%</Box>
                  <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>{sortIcon('progress')}</Box>
                </Box>
                <ResizeHandle onStart={(e) => { e.stopPropagation(); startResize(3, e); }} />
              </TableCell>
              <TableCell
                sx={{ ...thSortSx('customer', TH_SX), width: getW(5), ...frzTh(5), ...((customerFilter || excludedCustomers.size > 0) && { bgcolor: '#7c3aed', color: '#fff' }) }}
                onClick={() => {
                  if (customerFilter || excludedCustomers.size > 0) {
                    setCustomerFilter('');
                    setExcludedCustomers(new Set());
                  } else {
                    handleHeaderSortClick('customer');
                  }
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box>{L('ผู้ว่าจ้าง', 'Customer')}</Box>
                  <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>{sortIcon('customer')}</Box>
                </Box>
                <ResizeHandle onStart={(e) => { e.stopPropagation(); startResize(5, e); }} />
              </TableCell>
              <TableCell
                sx={{ ...thSortSx('name', TH_SX), width: getW(4), ...frzTh(4), ...((projectFilter || excludedProjects.size > 0) && { bgcolor: '#7c3aed', color: '#fff' }) }}
                onClick={() => {
                  if (projectFilter || excludedProjects.size > 0) {
                    setProjectFilter('');
                    setExcludedProjects(new Set());
                  } else {
                    handleHeaderSortClick('name');
                  }
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box>{L('โครงการ', 'Project')}</Box>
                  <Box sx={{ lineHeight: 1, position: 'relative', height: 0, overflow: 'visible' }}>{sortIcon('name')}</Box>
                </Box>
                <ResizeHandle onStart={(e) => { e.stopPropagation(); startResize(4, e); }} />
              </TableCell>

              {showGroup1 && (
                <>
                  <MoneyTh col={37} field="oldest_job_month" base={TH_SX} group>Mo</MoneyTh>
                  <MoneyTh col={38} field="oldest_job_year" base={TH_SX} group>Yr</MoneyTh>
                </>
              )}

              <MoneyTh col={6} field="po" base={TH_SX} hover="#7dd3fc">PO</MoneyTh>

              {showGroup1 && (
                <>
                  <MoneyTh col={7} field="billed_waiting" base={TH_GROUP_SX} hover="#6ba8a4" group>{L('Invoiced', 'Invoiced')}</MoneyTh>
                  <MoneyTh col={8} field="received" base={TH_GROUP_SX} hover="#6ba8a4" group>{L('รับเงิน', 'Received')}</MoneyTh>
                </>
              )}

              <MoneyTh col={9} field="po_remaining" base={TH_GROUP_SX} hover="#6ba8a4">{L('ยอดค้าง PO', 'PO Remain')}</MoneyTh>

              {showGroup1 && (
                <>
                  <MoneyTh col={18} field="target_profit" base={TH_GROUP4_SX} hover="#a7f3d0" group>{L('Profit Target', 'Profit Target')}</MoneyTh>
                  <MoneyTh col={33} field="total_budget" base={TH_GROUP2_SX} hover="#f9c4af" group>BG</MoneyTh>
                </>
              )}

              <MoneyTh col={11} field="total_expenses" base={TH_GROUP2_SX} hover="#f9c4af">{L('ใช้ไปแล้ว', 'Paid')}</MoneyTh>
              <MoneyTh col={10} field="projected_fund" base={TH_GROUP2_SX} hover="#f9c4af">{L('ทุนคาดการณ์', 'Projected')}</MoneyTh>

              {showGroup1 && (
                <>
                  <MoneyTh col={35} field="expected_value" base={TH_GROUP2_SX} hover="#f9c4af" group>{L('รอจ่าย', 'Expected')}</MoneyTh>
                  <MoneyTh col={32} field="ap_pending_unpaid" base={TH_GROUP2_SX} hover="#f9c4af" group>{L('AP รอจ่าย', 'AP Pending')}</MoneyTh>
                  <MoneyTh col={16} field="to_spend" base={TH_GROUP2_SX} hover="#f9c4af" group>{L('ต้องใช้อีก', 'To Spend')}</MoneyTh>
                  <MoneyTh col={12} field="col12_payable_bg_eligible" base={TH_GROUP2_SX} hover="#f9c4af" group>{L('BGเหลือสิทธิ', 'Eligible BG')}</MoneyTh>
                  <MoneyTh col={34} field="refund_pending" base={TH_GROUP2_SX} hover="#f9c4af" group>{L('รอเงินคืน', 'Refund')}</MoneyTh>
                </>
              )}

              <MoneyTh col={17} field="col17" base={TH_GROUP2_SX} hover="#f9c4af">{L('สรุปต้องจ่ายอีก', 'Total Payable')}</MoneyTh>

              {showGroup1 && (
                <>
                  <MoneyTh col={27} field="delivery_date" base={TH_GROUP5_SX} hover="#e9d5ff" group>{L('วันส่งงาน', 'Delivery')}</MoneyTh>
                  <MoneyTh col={28} field="credit_days" base={TH_GROUP5_SX} hover="#e9d5ff" group>{L('เครดิต', 'Credit')}</MoneyTh>
                  <MoneyTh col={31} field="payment_cycle_day" base={TH_GROUP5_SX} hover="#e9d5ff" group>{L('รอบจ่าย', 'Cycle')}</MoneyTh>
                  <MoneyTh col={29} field="expected_date" base={TH_GROUP5_SX} hover="#e9d5ff" group>{L('รับเงินวันที่', 'Pay Date')}</MoneyTh>
                  <MoneyTh col={30} field="days_left" base={TH_GROUP5_SX} hover="#e9d5ff" group>{L('อีก(วัน)', 'Days')}</MoneyTh>
                </>
              )}

              <MoneyTh col={15} field="net_remaining" base={TH_GROUP3_SX} hover="#60a5fa">{L('BG คงเหลือ', 'BG Remain')}</MoneyTh>
              <MoneyTh col={14} field="over_budget" base={TH_GROUP3_SX} hover="#60a5fa">{L('BG เกิน', 'Over Budget')}</MoneyTh>
              <MoneyTh col={19} field="realprofit" base={TH_GROUP3_SX} hover="#86efac" sortable={false}>{L('กำไร', 'Profit')}</MoneyTh>
              <MoneyTh col={13} field="bg_used_pct" base={TH_GROUP3_SX} hover="#fde68a">{L('%BG', '%BG')}</MoneyTh>
              <MoneyTh col={20} field="profit_pct" base={TH_GROUP3_SX} hover="#fde68a">{L('%กำไร', '%Profit')}</MoneyTh>
              <TableCell sx={{ ...TH_SX, width: getW(26) }}>{L('หมายเหตุ', 'Note')}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {displayRows.map((row, idx) => (
              <ProjectDashboardRow
                key={row._id}
                row={row}
                idx={idx}
                mergedRow={row}
                showCol2
                showGroup1={showGroup1}
                projectFilter={projectFilter}
                customerFilter={customerFilter}
                stickyLeftOf={stickyLeftOf}
                getW={getW}
                getVal={getVal}
                getCustomerName={getCustomerName}
                sameCustomerAsPrev={idx > 0 && getCustomerName(row) === getCustomerName(displayRows[idx - 1])}
                onClickProject={() => {}}
                scheduleClick={scheduleClick}
                cancelAndRun={cancelAndRun}
                setProjectFilter={setProjectFilter}
                setCustomerFilter={setCustomerFilter}
                onToggleExcludeProject={toggleExcludedProject}
                onToggleExcludeCustomer={toggleExcludedCustomer}
                monthFilter={monthFilter}
                yearFilter={yearFilter}
                setMonthFilter={setMonthFilter}
                setYearFilter={setYearFilter}
                onToggleExcludeMonth={() => {}}
                onToggleExcludeYear={() => {}}
                onToggleZeroExclude={toggleZeroExclude}
                renderSeedCell={renderSeedCell}
                todayReviewStr={todayReviewStr}
                isPinned={false}
                onOpenActionRequired={openActionDialog}
              />
            ))}
            {displayRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={showGroup1 ? 31 : 13} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                  {L('ไม่พบข้อมูล', 'No data')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!actionDialog} onClose={closeActionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{actionDialog?.mode === 'remove' ? 'ปลดป้าย Action required' : 'เพิ่มป้าย Action required'}</DialogTitle>
        <DialogContent>
          {actionDialog?.mode === 'remove' ? (
            <Typography variant="body2" color="text.secondary">
              {actionDialog?.row?.action_required?.text}
            </Typography>
          ) : (
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={3}
              margin="dense"
              label="Action required"
              value={actionText}
              inputProps={{ maxLength: 500 }}
              onChange={(e) => setActionText(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeActionDialog}>ยกเลิก</Button>
          <Button variant="contained" color={actionDialog?.mode === 'remove' ? 'error' : 'warning'} onClick={submitActionDialog}>
            {actionDialog?.mode === 'remove' ? 'ปิดป้าย' : 'บันทึก'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
